import React, { useContext, useMemo, useState } from "react";
import { gql, useQuery } from "@apollo/client";
import Spinner from "../../components/Spinner";
import ConfirmModal from "../../components/modals/ConfirmModal";
import EntityOperationBar from "../../components/EntityOperationBar";
import JobSchedulesCalendarView from "./JobSchedulesCalendarView";
import JobSchedulesListingView from "./JobSchedulesListingView";
import JobSchedulesSidebar from "./JobSchedulesSidebar";
import StarekContext from "../../context/StarekContext";

const JobSchedules = (props) => {
    const starekContext = useContext(StarekContext);
    const [viewMode, setViewMode] = useState("calendar");
    const [selectedRows, setSelectedRows] = useState([]);
    const [filterText, setFilterText] = useState("");
    const [toggleCleared, setToggleCleared] = useState(false);
    const [showSidebar, setShowSidebar] = useState(false);
    const [jobToEdit, setJobToEdit] = useState(null);
    const [confirmCfg, setConfirmCfg] = useState({ open: false });
    const [isDeleting, setIsDeleting] = useState(false);
    const [deleteDone, setDeleteDone] = useState(0);

    const GET_JOB_SCHEDULES = gql`
        query GetJobSchedules($plantId: Int!) {
            jobSchedules(where: { plant_id: { equals: $plantId } }) {
                id
                name
                created_at
                updated_at
                created_by {
                    id
                    name
                }
                updated_by {
                    id
                    name
                }
                description
                cycle {
                    created_at
                    description
                    id
                    is_active
                    name
                    plant_id
                    plant {
                        code
                        name
                    }
                }
                cycle_id
                part_id
                part {
                    id
                    name
                    plant_id
                    description
                    is_active
                }
                device_id
                end_time
                start_time
                plant_id
                plant {
                    name
                    id
                }
            }
        }
    `;

    const { data, loading, error, refetch } = useQuery(GET_JOB_SCHEDULES, {
        client: starekContext.engineClient,
        variables: { plantId: starekContext.lastUsedPlant.id },
    });

    const jobSchedules = data?.jobSchedules ?? [];

    const handleAdd = () => {
        setShowSidebar(true);
    };

    const handleEdit = () => {
        if (selectedRows.length !== 1) return;
        setShowSidebar(true);
    };

    const handleBulkDelete = async () => {
        setIsDeleting(true);
        setDeleteDone(0);
        const lastUsedWorkspace = starekContext?.lastUsedWorkspace;
        const token = starekContext?.auth;
        const { host, scheme, port } = lastUsedWorkspace?.engine || {};
        const apiUrl = `${scheme}://${host}:${port}/api/v1/${lastUsedWorkspace.name}/graphql`;

        try {
            let completed = 0;
            for (const job of selectedRows) {
                const query = {
                    query: `
                        mutation DeleteOneJobSchedule {
                            deleteOneJobSchedule(where: { id: ${job.id} }) { id }
                        }
                    `,
                };
                await fetch(apiUrl, {
                    method: "POST",
                    headers: {
                        "Content-Type": "application/json",
                        Authorization: `Bearer ${token}`,
                    },
                    body: JSON.stringify(query),
                });
                completed++;
                setDeleteDone(completed);
            }
            await refetch();
            setSelectedRows([]);
            setToggleCleared(!toggleCleared);
            setConfirmCfg({ open: false });
        } catch (error) {
            console.error("Error deleting job schedules:", error);
            alert("Failed to delete some job schedules. Please try again.");
        } finally {
            setIsDeleting(false);
            setDeleteDone(0);
        }
    };

    const handleSidebarClose = async () => {
        setShowSidebar(false);
        setJobToEdit(null);
        await refetch();
    };

    return loading ? (
        <div
            className="d-flex flex-column justify-content-center align-items-center"
            style={{ minHeight: "100vh" }}
        >
            <div className="text-xs d-flex justify-content-center">
                <i className="fa fa-calendar fa-10x text-success"></i>
            </div>
            <Spinner />
        </div>
    ) : error ? (
        <div className="tw-text-center tw-mt-10 tw-text-red-600">
            Failed to load job schedules: {error.message}
        </div>
    ) : (
        <div className="container-fluid">
            <div className="font-weight-bold mb-3">Job Schedules</div>

            <EntityOperationBar
                entity={{ singular: "Job Schedule", plural: "Job Schedules" }}
                allowSearch={true}
                allowAdd={true}
                allowDelete={true}
                allowEdit={true}
                selectedRows={selectedRows}
                isDeleting={isDeleting}
                deleteDone={deleteDone}
                onSearch={(text) => setFilterText(text)}
                onAdd={handleAdd}
                onViewChange={(mode) => {
                    setViewMode(mode);
                    setSelectedRows([]);
                }}
                onEdit={handleEdit}
                onDelete={handleBulkDelete}
            >
                <EntityOperationBar.View
                    mode="calendar"
                    icon="fa fa-calendar"
                    tooltip="Calendar View"
                >
                    <JobSchedulesCalendarView
                        jobSchedules={jobSchedules}
                        selectedRows={selectedRows}
                        onSelectedRowsChange={(state) =>
                            setSelectedRows(state.selectedRows)
                        }
                        onRefresh={refetch}
                        filterText={filterText}
                    />
                </EntityOperationBar.View>

                <EntityOperationBar.View
                    mode="list"
                    icon="fa fa-list"
                    tooltip="List View"
                >
                    <JobSchedulesListingView
                        items={jobSchedules}
                        selectedRows={selectedRows}
                        onSelectedRowsChange={(state) =>
                            setSelectedRows(state.selectedRows)
                        }
                        filterText={filterText}
                        toggleCleared={toggleCleared}
                    />
                </EntityOperationBar.View>
            </EntityOperationBar>

            <JobSchedulesSidebar
                isOpen={showSidebar}
                onClose={handleSidebarClose}
                onSubmit={handleSidebarClose}
                type={selectedRows[0] ? "edit" : "add"}
                jobData={selectedRows[0] || null}
            />
            <ConfirmModal {...confirmCfg} />
        </div>
    );
};

export { JobSchedules };
